/* global setTimeout, clearTimeout */
/**
 * usePagedServerList.js
 *
 * Server-side paging + debounced search for long lists (work orders, service
 * customers, …). The screens used to hold a store's WHOLE collection in memory
 * and re-filter / re-sort it on every keystroke; with thousands of imported
 * records that froze the JS thread. This hook keeps only the pages the user has
 * actually scrolled to and lets the backend do the searching.
 *
 * Contract of `fetchPage({page, limit, search, signalId})`: resolve
 * `{items, total, pages}` (rows already normalised). The hook guarantees:
 *  - search typing is debounced (cleared search applies immediately);
 *  - a response that arrives after a newer request was issued is DROPPED
 *    (no stale-response race when the user keeps typing / switching filters);
 *  - `loadMore` never fires twice for the same page and de-duplicates rows by `_id`;
 *  - `refreshKey` changes trigger a silent first-page reload (the rows stay on
 *    screen — no spinner flash) after a short coalescing delay.
 *
 * @module hooks/usePagedServerList
 */
import {useCallback, useEffect, useRef, useState} from 'react';

/** Default delay between the last keystroke and the search request (ms). @type {number} */
export const SEARCH_DEBOUNCE_MS = 300;

/** Coalescing delay for `refreshKey` driven silent reloads (ms). @type {number} */
const REFRESH_COALESCE_MS = 500;

const mergeById = (current, incoming) => {
    const seen = new Set(current.map((row) => String(row?._id)));
    const fresh = incoming.filter((row) => !seen.has(String(row?._id)));

    return fresh.length ? [...current, ...fresh] : current;
};

/**
 * @param {object} options
 * @param {(args: {page: number, limit: number, search: string, skipCache: boolean}) => Promise<{items: object[], total?: number, pages?: number}>} options.fetchPage
 *   Page reader; read through a ref, so an inline arrow is fine.
 * @param {string} [options.search=''] Live search box text.
 * @param {string} [options.queryKey=''] Any other query identity (e.g. the status filter); a change reloads page 1.
 * @param {number} [options.limit=30] Page size.
 * @param {number|string} [options.refreshKey=0] Bump to silently reload page 1 (e.g. after a local save).
 * @param {object[]} [options.seed] Rows to show instantly while the first page loads (only used before the first response).
 * @param {boolean} [options.enabled=true] Set false to hold requests (e.g. screen not ready).
 * @param {number} [options.debounceMs=SEARCH_DEBOUNCE_MS] Search debounce.
 * @returns {{items: object[], total: number|null, hasMore: boolean, loading: boolean, loadingMore: boolean,
 *   refreshing: boolean, error: string, loadMore: () => void, refresh: () => Promise<void>, debouncedSearch: string}}
 */
export default function usePagedServerList({
    fetchPage,
    search = '',
    queryKey = '',
    limit = 30,
    refreshKey = 0,
    seed = null,
    enabled = true,
    debounceMs = SEARCH_DEBOUNCE_MS,
}) {
    const fetchRef = useRef(fetchPage);
    fetchRef.current = fetchPage;

    const mountedRef = useRef(true);
    const requestIdRef = useRef(0);
    const pageRef = useRef(0);
    const pagesRef = useRef(1);
    const loadingMoreRef = useRef(false);
    const searchRef = useRef('');

    const [items, setItems] = useState(() => (Array.isArray(seed) ? seed : []));
    const [total, setTotal] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(!!enabled);
    const [loadingMore, setLoadingMore] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState(String(search || '').trim());

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const next = String(search || '').trim();

        if (next === debouncedSearch) {
            return undefined;
        }

        const handle = setTimeout(() => setDebouncedSearch(next), next ? debounceMs : 0);

        return () => clearTimeout(handle);
    }, [search, debouncedSearch, debounceMs]);

    searchRef.current = debouncedSearch;

    /**
     * Load one page. `replace` swaps the rows (first page), `append` adds them.
     * `silent` keeps the rows on screen and skips the loading flag.
     */
    const run = useCallback(async ({page, append = false, silent = false, skipCache = false}) => {
        const requestId = ++requestIdRef.current;

        if (append) {
            loadingMoreRef.current = true;
            setLoadingMore(true);
        } else if (!silent) {
            setLoading(true);
        }

        try {
            const result = await fetchRef.current({page, limit, search: searchRef.current, skipCache});

            if (!mountedRef.current || requestId !== requestIdRef.current) {
                return;
            }

            const rows = Array.isArray(result?.items) ? result.items : [];
            const pages = Number(result?.pages) > 0 ? Number(result.pages) : 1;

            pageRef.current = page;
            pagesRef.current = pages;
            setItems((prev) => (append ? mergeById(prev, rows) : rows));
            setTotal(Number.isFinite(Number(result?.total)) ? Number(result.total) : null);
            setHasMore(page < pages);
            setError('');
        } catch (err) {
            if (mountedRef.current && requestId === requestIdRef.current) {
                setError(String(err?.message || err || 'error'));
                if (append) {
                    // Allow a retry of the same page on the next end-reached.
                    pageRef.current = page - 1;
                }
            }
        } finally {
            if (mountedRef.current && requestId === requestIdRef.current) {
                loadingMoreRef.current = false;
                setLoadingMore(false);
                setLoading(false);
                setRefreshing(false);
            }
        }
    }, [limit]);

    // A different filter (queryKey) is a different list: drop the old rows right away instead of
    // showing them under the new filter until the answer arrives. (Typing in the search box keeps
    // the previous rows on screen until the new results land — no empty flash per keystroke.)
    const firstQueryKeyRef = useRef(true);
    useEffect(() => {
        if (firstQueryKeyRef.current) {
            firstQueryKeyRef.current = false;

            return;
        }

        setItems([]);
        setHasMore(false);
        pageRef.current = 0;
    }, [queryKey]);

    // New query (search text settled / filter changed): back to page 1.
    useEffect(() => {
        if (!enabled) {
            return;
        }

        loadingMoreRef.current = false;
        run({page: 1});
    }, [debouncedSearch, queryKey, enabled, run]);

    // Local mutation elsewhere (save / create / delete): silent first-page reload.
    const firstRefreshRef = useRef(true);

    useEffect(() => {
        if (firstRefreshRef.current) {
            firstRefreshRef.current = false;

            return undefined;
        }

        if (!enabled) {
            return undefined;
        }

        const handle = setTimeout(() => run({page: 1, silent: true, skipCache: true}), REFRESH_COALESCE_MS);

        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    const loadMore = useCallback(() => {
        if (!enabled || loadingMoreRef.current || pageRef.current < 1 || pageRef.current >= pagesRef.current) {
            return;
        }

        run({page: pageRef.current + 1, append: true});
    }, [enabled, run]);

    const refresh = useCallback(async () => {
        setRefreshing(true);
        await run({page: 1, silent: true, skipCache: true});
    }, [run]);

    return {items, total, hasMore, loading, loadingMore, refreshing, error, loadMore, refresh, debouncedSearch};
}
